import type { EditKind } from "./graduation";
import { SCENARIO_MAP, type Scenario } from "./scenario";
import { SHADOW_ARTIFACT_VERSIONS_UNREADABLE } from "./shadowExit";
import { INQUIRY_TYPES, type Channel, type InquiryType } from "./types";

/**
 * Design queue row Q1 (docs/DESIGN-learn-qualify-2026-10-03.md, part C, metrics
 * that do not forget): the codes-only stamp a later item (Q17) writes at the
 * moment the owner approves, rejects or withholds a draft.
 *
 * WHY A STAMP. Kabir, typed 2026-10-03: "metrics shoulnd't forget what aspects
 * worked because thy are all correlated as they should". The joins that answer
 * "what worked" read rows that erasure deletes (design, "What forgets today"
 * item 7), and the routing event stores the customer's own matched words
 * (`routing.decided.matched`, design D31). A stamp written at the decision, out
 * of codes alone, keeps the combination that produced an outcome without keeping
 * anything a customer said.
 *
 * CUSTODY, THE WHOLE POINT (CLAUDE.md section 10). This takes no message field.
 * Its input type has no `matched`, no route, no text, no story or post id, and
 * the type is exact, so a variable that carries extra keys is refused at compile
 * time as well as a fresh literal. At run time, where the input is data nobody
 * typed, every key is checked against the allowlist and every value against a
 * closed vocabulary, an id shape, or an existing type. Anything else throws an
 * `AttributionStampError` (CLAUDE.md section 0: fail loudly, never drop quietly)
 * that names the FIELD and the rule, and never quotes the value or the runtime's
 * own message, because a refusal that echoes the leak is a second leak.
 *
 * WHAT IT DOES NOT DO. Nothing calls it yet: no writer, no event, no sender, no
 * prompt reads it. Q17 wires it, and wiring is a separate item with its own
 * tests. It also cannot tell an id from one lowercase word: an id shape is a
 * bound, not proof. The protection is that the callers pass ids the system
 * made (a pattern id, a model id), never words a customer typed, and that the
 * shape and the caps keep a stamp small.
 *
 * ABSENCE IS EXPLICIT. Every key is required. Where a value can honestly be
 * absent the input says so with `null` (no model on a holding line, no label
 * recorded, no edit on a reject or a withhold, a first pass not recorded), so a
 * forgotten field is a refusal and never a quiet null (the same distinction the
 * scenario map draws between "n/a" and not recorded).
 *
 * TIME IS COARSE AND THE SHOP'S OWN. The stamp holds a day part and a weekday or
 * weekend flag, never the instant. Both are read in the shop's time zone, so a
 * Friday night order is a weekday order whatever UTC says. The one existing
 * helper for a shop-local day is `dayKey` (packages/gateway/src/budget.ts), and
 * core cannot import the gateway (core has no workspace dependencies), so the
 * local weekday and hour are read here the same way `dayKey` reads the local
 * date: `Intl.DateTimeFormat` with an explicit zone, and an unknown zone THROWS
 * instead of falling back to UTC. packages/gateway/src/attributionBucketZone.test.ts
 * holds the two readings to agreeing about which local day an instant is in.
 * The caller passes the zone the budget day already uses, so the system has one
 * definition of the shop's day (`Gateway#budgetDayTimeZone`).
 *
 * DETERMINISTIC. The same input gives the same stamp, byte for byte: keys in
 * allowlist order, id lists sorted, the result frozen. Two instants in the same
 * bucket give the same stamp.
 *
 * Every number and list below that a ruling may change carries the marker for
 * design D33, on one line, so the ratification sheet reads it. Nothing here is
 * ruled.
 */

/** The file the markers below cite. One spelling, so the sheet and the test agree. */
// (kept in the marker lines themselves; a constant would hide it from the sheet)

// ---------------------------------------------------------------------------
// The allowlist.
// ---------------------------------------------------------------------------

/**
 * The keys a stamp has, in the order it is written. The allowlist Q17's payload
 * test pins. The last two, `windowOmitted` and `memoryOmitted`, are loop f item
 * OMIT (2026-10-03): counts of what the reply prompt's thread window and contact
 * memory summary dropped, so forgetting is visible to `measure:*`. Counts only,
 * and null (not recorded) is never read as zero. AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): the list is the design's Q1 row, and a key added later is a custody decision.
 */
export const ATTRIBUTION_STAMP_KEYS = [
  "scenario",
  "mapVersion",
  "framework",
  "moveIds",
  "signOffIds",
  "channel",
  "entrySourceKind",
  "inquiryType",
  "dayPart",
  "dayType",
  "composeModel",
  "configurationLabel",
  "editKind",
  "firstPassClean",
  "windowOmitted",
  "memoryOmitted",
] as const;

export type AttributionStampKey = (typeof ATTRIBUTION_STAMP_KEYS)[number];

/**
 * B5 (revenue build list, ruling 6): keys a stamp carries only when the draft belonged to a running experiment.
 * They are NOT in the fixed list above on purpose: a stamp outside an experiment is byte-for-byte what it was
 * before B5 (no key, no null), so no stored stamp and no pinned shape moves. They follow the fixed keys, in this
 * order. The arm is two ids the registry minted (`experiment_registrations`), never a word a customer typed.
 * AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): adding this key to the allowlist is a custody decision.
 */
export const ATTRIBUTION_OPTIONAL_STAMP_KEYS = ["experimentArm"] as const;

/** The optional input keys: absent, undefined and null all mean "not in an experiment". */
const OPTIONAL_INPUT_KEYS: readonly string[] = ATTRIBUTION_OPTIONAL_STAMP_KEYS;

/** The keys the INPUT has: the stamp's, with the instant and the zone standing in for the two buckets read from them. */
const INPUT_KEYS = [
  "scenario",
  "mapVersion",
  "framework",
  "moveIds",
  "signOffIds",
  "channel",
  "entrySourceKind",
  "inquiryType",
  "at",
  "shopTimeZone",
  "composeModel",
  "configurationLabel",
  "editKind",
  "firstPassClean",
  "windowOmitted",
  "memoryOmitted",
] as const;

// ---------------------------------------------------------------------------
// The closed vocabularies.
// ---------------------------------------------------------------------------

/**
 * Where a thread started, as a kind and never an id. The first three are the
 * kinds the webhook parser already produces (`IngestKind` in
 * packages/pipeline/src/webhook/parse.ts, which core cannot import): a plain
 * DM (`message`), a reply to a story (`story_reply`), a comment on a post
 * (`comment`). `unknown` is not recorded. There is no `ad` kind because no code
 * records an ad id yet (salesMeasure.ts), and a kind with no writer would only
 * be a word nothing can say truthfully. The story or post id itself never
 * enters a stamp, which holds until counsel answers F-26 (design part C).
 * AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): the list of kinds.
 */
export const ATTRIBUTION_ENTRY_SOURCE_KINDS = ["message", "story_reply", "comment", "unknown"] as const;
export type AttributionEntrySourceKind = (typeof ATTRIBUTION_ENTRY_SOURCE_KINDS)[number];

/**
 * The day parts, local to the shop, in clock order: overnight 00:00 to 05:59,
 * morning 06:00 to 11:59, afternoon 12:00 to 17:59, evening 18:00 to 23:59. Four
 * blocks of the same length, the coarsest split that still separates a morning
 * rush from an evening browse, and wide enough that a bucket cannot single out a
 * visit. AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): the names and the order.
 */
export const ATTRIBUTION_DAY_PARTS = ["overnight", "morning", "afternoon", "evening"] as const;
export type AttributionDayPart = (typeof ATTRIBUTION_DAY_PARTS)[number];

/**
 * How many hours each day part spans. AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): six, so the boundaries fall on 06:00, 12:00 and 18:00.
 */
export const ATTRIBUTION_DAY_PART_HOURS = 6;

/**
 * Weekday or weekend, by the shop's local calendar day. The weekend is Saturday
 * and Sunday, the conservative common reading; a shop whose weekend is another
 * pair of days is a ruling, not something to guess. AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): which days are the weekend.
 */
export const ATTRIBUTION_DAY_TYPES = ["weekday", "weekend"] as const;
export type AttributionDayType = (typeof ATTRIBUTION_DAY_TYPES)[number];

/** `Intl`'s short English weekday names for the weekend days, read with a fixed locale so the process locale cannot move them. */
const WEEKEND_WEEKDAYS: readonly string[] = ["Sat", "Sun"];

/** Every scenario row, read from the map so a new scenario is a legal value the day it exists. */
const SCENARIOS: readonly string[] = Object.keys(SCENARIO_MAP);

/** Every lead framework the map names, once each, including the one named none. */
const FRAMEWORKS: readonly string[] = [...new Set(Object.values(SCENARIO_MAP).map((row) => row.leadFramework))];

/**
 * Channels and edit kinds are string unions with no runtime list. Declared as
 * records over the union, so adding a member to `Channel` or `EditKind` is a
 * compile error here until the stamp says whether it is a legal value.
 */
const CHANNEL_NAMES = {
  instagram: true,
  facebook: true,
  instagram_comment: true,
  facebook_comment: true,
  sms: true,
  email: true,
  webchat: true,
} as const satisfies Record<Channel, true>;
const CHANNELS: readonly string[] = Object.keys(CHANNEL_NAMES);

const EDIT_KIND_NAMES = { unedited: true, cosmetic: true, substantive: true } as const satisfies Record<EditKind, true>;
const EDIT_KINDS: readonly string[] = Object.keys(EDIT_KIND_NAMES);

const INQUIRY_VALUES: readonly string[] = [...INQUIRY_TYPES, "unknown"];

// ---------------------------------------------------------------------------
// Id shapes and caps.
// ---------------------------------------------------------------------------

/**
 * A learned move or a sign-off is named by the Playbook's pattern id
 * (`price_objection_hold`): lower case words joined by single underscores. The
 * repo defines no pattern for that id, only the examples in `PATTERN_DEFS`
 * (packages/pipeline/src/stages/stage4.ts), and this shape fits every one of
 * them: it is a bound, not a definition. It is strict on purpose. A real id
 * outside it is refused loudly and widened by a ruling, which is the safe
 * direction.
 * AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): the id shape, lower case words and digits joined by single underscores, starting with a letter.
 */
const ID_SHAPE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/**
 * The longest an id may be. AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): sixty-four characters.
 */
const ID_MAX_CHARS = 64;

/**
 * The most move ids, and the most sign-off ids, one stamp may carry. A longer
 * list is refused and never cut, because a stamp that quietly dropped a move
 * would credit an outcome to a combination that did not include it.
 * AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): twenty, the same number the design loads into a prompt at most.
 */
const ID_LIST_MAX = 20;

/** The scenario map's version string: `scenario-map-v6`. Any past version is legal, since a stamp is about one decision under the map of its day. */
const MAP_VERSION_SHAPE = /^scenario-map-v[0-9]{1,3}$/;
const MAP_VERSION_MAX_CHARS = 32;

/**
 * A model id as `drafts.compose_model` stores it: `claude-opus-5`, a dated pin
 * (`claude-haiku-4-5-20251001`), or the id a mock provider claims, which is
 * `<name>/v1` (`mock-frontier/v1`, MockLLMProvider's default, and the model of
 * every mock-composed draft until a real chain is wired). So one lower case id,
 * with at most one slash and a second segment after it, and no more.
 * packages/gateway/src/attributionModelId.test.ts reads the ids off the real
 * providers and the price table and requires this shape to take every one.
 * AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): the model id shape, one lower case id with at most one slash segment.
 */
const MODEL_SHAPE = /^[a-z][a-z0-9._-]*(?:\/[a-z0-9._-]+)?$/;

/**
 * The longest a model id may be. AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): sixty-four characters.
 */
const MODEL_MAX_CHARS = 64;

/**
 * The artifact kinds a configuration label names, in the order the label lists
 * them: the runtime kinds `describeDraftArtifactVersions`
 * (packages/db/src/artifactSource.ts) writes, `RUNTIME_ARTIFACT_KINDS` there.
 * Core cannot import that package, so the list is held here by hand and
 * packages/db/src/attributionLabel.test.ts requires the two to be equal and
 * takes the producer's own output through the stamp. A kind added to the
 * producer without a ruling here is refused loudly, never passed through.
 * AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): the label kinds, and their order.
 */
export const ATTRIBUTION_LABEL_KINDS = ["kb", "voice", "playbook", "gold"] as const;

/**
 * The configuration label, in the shape the producer writes: each runtime kind
 * once, in order, with its version and a twelve-character content hash prefix,
 * then, when sign-offs are in force, `signed` and their ids sorted and joined
 * by `+`. The signed tail is captured and held to the same id rules as
 * `signOffIds` (shape, 64 characters each, 20 at most, no repeats), so the one
 * free-form part of the label cannot carry more than a sign-off list can.
 */
const LABEL_SEGMENT = (kind: string): string => `${kind} v[0-9]{1,6} [0-9a-f]{12}`;
const LABEL_SHAPE = new RegExp(`^${ATTRIBUTION_LABEL_KINDS.map(LABEL_SEGMENT).join(", ")}(?:, signed ([a-z0-9_+]*))?$`);

/**
 * The longest a label may be, checked before the pattern runs so an enormous
 * string is refused cheaply. AWAITING RATIFICATION (design D33, docs/DESIGN-learn-qualify-2026-10-03.md): 2048 characters.
 */
const LABEL_MAX_CHARS = 2048;

/** The longest a time zone name may be. IANA names are under 40; a longer string is not one. */
const ZONE_MAX_CHARS = 64;

/**
 * The unknown keys a refusal may NAME: the fields a route, a message or a
 * contact carries (the ones a caller spreading a record into this function
 * would pass), and the stamp's own two bucket keys, which are output and not
 * input. These are fixed identifiers this file reads nothing from. Every other
 * unknown key is reported as `(unnamed key)`: a key is data too, and a handle,
 * a name or a one-word phrase used as a key is a customer's words, which a
 * thrown message would carry into a log or an alert (CLAUDE.md section 10).
 * A new field worth naming is added here by hand.
 */
export const ATTRIBUTION_NAMED_REFUSED_KEYS = [
  "matched",
  "turnsBack",
  "route",
  "leadFramework",
  "graduationClass",
  "specialGates",
  "generates",
  "text",
  "body",
  "content",
  "message",
  "messageText",
  "messageId",
  "threadId",
  "contactId",
  "name",
  "storyId",
  "postId",
  "entrySource",
  "dayPart",
  "dayType",
] as const;
const NAMED_REFUSED_KEYS: readonly string[] = ATTRIBUTION_NAMED_REFUSED_KEYS;

// ---------------------------------------------------------------------------
// The refusal.
// ---------------------------------------------------------------------------

export type AttributionStampRefusalReason =
  | "not_an_object"
  | "unknown_key"
  | "missing_key"
  | "wrong_type"
  | "not_in_vocabulary"
  | "not_an_id"
  | "too_long"
  | "too_many"
  | "duplicate"
  | "bad_instant"
  | "unknown_time_zone"
  | "unreadable_time"
  | "not_a_count";

const REASON_TEXT: Readonly<Record<AttributionStampRefusalReason, string>> = {
  not_an_object: "the input is not a plain object",
  unknown_key: "that key is not on the allowlist",
  missing_key: "the key is required and absent (use null where absence is real)",
  wrong_type: "the value is not the type this field takes",
  not_in_vocabulary: "the value is not in this field's closed list",
  not_an_id: "the value is not an id of the shape this field takes",
  too_long: "the value is longer than this field allows",
  too_many: "the list is longer than this field allows",
  duplicate: "the list names an id twice",
  bad_instant: "the moment is not a valid Date",
  unknown_time_zone: "the shop time zone is not one this runtime knows, and guessing UTC would move every bucket without saying so",
  unreadable_time: "the runtime could not read the shop's local day and hour",
  not_a_count: "the value is not a non-negative whole number (use null where the count was not recorded)",
};

/**
 * What the stamp throws. `field` and `reason` are codes; the message is built
 * from them alone, so no value, key sentence or runtime message can reach it.
 * A caller that must tell this refusal from a dropped connection catches the
 * class, the way `WriteTierRefusal` is caught.
 */
export class AttributionStampError extends Error {
  /** An allowlisted key, `(input)` for the argument as a whole, a key on `ATTRIBUTION_NAMED_REFUSED_KEYS`, or `(unnamed key)` for any other unknown key. */
  readonly field: string;
  readonly reason: AttributionStampRefusalReason;

  constructor(field: string, reason: AttributionStampRefusalReason) {
    super(`attributionStamp refused ${field}: ${REASON_TEXT[reason]}. The stamp holds codes only, so a value outside its lists is refused, never cut or guessed.`);
    this.name = "AttributionStampError";
    this.field = field;
    this.reason = reason;
  }
}

// ---------------------------------------------------------------------------
// Types.
// ---------------------------------------------------------------------------

/**
 * Everything a stamp is made from, and nothing else. There is deliberately no
 * route, no `matched`, no message or thread field, and no entry source id: the
 * caller reads `route.scenario`, `route.mapVersion` and the row's lead framework
 * off the route and hands over those three codes.
 */
export interface AttributionStampInput {
  readonly scenario: Scenario;
  /** The scenario map's version string, as `SCENARIO_MAP_VERSION` writes it. */
  readonly mapVersion: string;
  /** The scenario row's lead framework, one of the map's own values. */
  readonly framework: string;
  /** The learned moves the draft used, as pattern ids. Empty when none. */
  readonly moveIds: readonly string[];
  /** The owner sign-offs in force when the draft was composed, as pattern ids. Empty when none. */
  readonly signOffIds: readonly string[];
  readonly channel: Channel;
  /** Where the thread started, as a kind. `unknown` when not recorded. */
  readonly entrySourceKind: AttributionEntrySourceKind;
  /** The inquiry type, or `unknown`. */
  readonly inquiryType: InquiryType | "unknown";
  /** The moment of the decision. Only its buckets are kept. */
  readonly at: Date;
  /** The shop's IANA time zone, the one its budget day uses. */
  readonly shopTimeZone: string;
  /** The model that composed the draft; null for a holding line, which no model writes. */
  readonly composeModel: string | null;
  /** The configuration label (kb, voice, playbook and gold versions, then sign-offs); null when not recorded. */
  readonly configurationLabel: string | null;
  /** The edit kind of an approved draft; null on a reject or a withhold, which approve no text. */
  readonly editKind: EditKind | null;
  /** Whether the first pass was clean (F-25); null when not recorded. */
  readonly firstPassClean: boolean | null;
  /**
   * Turns the reply prompt's thread window dropped: the `omitted` the prompt
   * printed ("N earlier turns are not shown"), so forgetting can be joined to
   * owner edits, escalations and ghosted threads. A count, never the turns.
   * Null when not recorded, which is NOT zero.
   */
  readonly windowOmitted: number | null;
  /** Stored memories the prompt's contact memory summary dropped, from the same `omitted` the prompt printed. Null when not recorded, which is NOT zero. */
  readonly memoryOmitted: number | null;
  /**
   * B5: the experiment and the arm this draft was assigned to, as two ids. Absent or null when the draft was not in
   * an experiment, and then the stamp has no such key.
   */
  readonly experimentArm?: AttributionExperimentArm | null;
}

/** An experiment id and an arm id, each an id of the shape `ID_SHAPE` takes. */
export interface AttributionExperimentArm {
  readonly experimentId: string;
  readonly armId: string;
}

/** The input, refusing any key it does not declare, even on a variable (a fresh literal already gets excess property checks). */
export type ExactAttributionStampInput<T> = T & Record<Exclude<keyof T, keyof AttributionStampInput>, never>;

/** The stamp: codes only, keys in `ATTRIBUTION_STAMP_KEYS` order, frozen. */
export interface AttributionStamp {
  readonly scenario: Scenario;
  readonly mapVersion: string;
  readonly framework: string;
  readonly moveIds: readonly string[];
  readonly signOffIds: readonly string[];
  readonly channel: Channel;
  readonly entrySourceKind: AttributionEntrySourceKind;
  readonly inquiryType: InquiryType | "unknown";
  readonly dayPart: AttributionDayPart;
  readonly dayType: AttributionDayType;
  readonly composeModel: string | null;
  readonly configurationLabel: string | null;
  readonly editKind: EditKind | null;
  readonly firstPassClean: boolean | null;
  readonly windowOmitted: number | null;
  readonly memoryOmitted: number | null;
  /** Present only for a draft that was in an experiment (B5). */
  readonly experimentArm?: AttributionExperimentArm;
}

export interface AttributionTimeBuckets {
  readonly dayPart: AttributionDayPart;
  readonly dayType: AttributionDayType;
}

// ---------------------------------------------------------------------------
// Validators. Each returns the value, narrowed, or throws; none quotes a value.
// ---------------------------------------------------------------------------

function refuse(field: string, reason: AttributionStampRefusalReason): never {
  throw new AttributionStampError(field, reason);
}

/**
 * Membership is `includes` on a list, which compares by SameValueZero and
 * coerces nothing, so `"toString"`, `"__proto__"` and an object whose
 * `toString` returns a legal word are all outside it (the TA-391 trap a keyed
 * lookup falls into).
 */
function oneOf<T extends string>(field: string, value: unknown, allowed: readonly string[]): T {
  if (typeof value !== "string") return refuse(field, "wrong_type");
  if (!allowed.includes(value)) return refuse(field, "not_in_vocabulary");
  return value as T;
}

function shaped(field: string, value: unknown, shape: RegExp, maxChars: number): string {
  if (typeof value !== "string") return refuse(field, "wrong_type");
  if (value.length > maxChars) return refuse(field, "too_long");
  if (!shape.test(value)) return refuse(field, "not_an_id");
  return value;
}

function idList(field: string, value: unknown): readonly string[] {
  if (!Array.isArray(value)) return refuse(field, "wrong_type");
  if (value.length > ID_LIST_MAX) return refuse(field, "too_many");
  const ids: string[] = [];
  // An index loop, not map: a sparse array's holes are read as undefined and refused.
  for (let i = 0; i < value.length; i++) ids.push(shaped(field, value[i], ID_SHAPE, ID_MAX_CHARS));
  if (new Set(ids).size !== ids.length) return refuse(field, "duplicate");
  // Plain sort: ids are ASCII by shape, so code unit order is the same in every locale.
  return Object.freeze(ids.sort());
}

/** Whether `value` has the shape of an id a stamp keeps (lower case words joined by single underscores, 64 characters at most). The registry uses the same test for experiment and arm ids. */
export function isAttributionId(value: unknown): boolean {
  return typeof value === "string" && value.length <= ID_MAX_CHARS && ID_SHAPE.test(value);
}

/** Reads the optional arm: undefined or null is none, an object of exactly two ids is kept, anything else is refused under one field. */
function readExperimentArm(value: unknown): AttributionExperimentArm | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "object" || Array.isArray(value)) return refuse("experimentArm", "not_an_object");
  for (const key of Reflect.ownKeys(value)) {
    if (key !== "experimentId" && key !== "armId") return refuse("experimentArm", "unknown_key");
  }
  const record = value as Record<string, unknown>;
  if (!Object.hasOwn(record, "experimentId") || !Object.hasOwn(record, "armId")) return refuse("experimentArm", "missing_key");
  const experimentId = shaped("experimentArm", record["experimentId"], ID_SHAPE, ID_MAX_CHARS);
  const armId = shaped("experimentArm", record["armId"], ID_SHAPE, ID_MAX_CHARS);
  return Object.freeze({ experimentId, armId });
}

function nullable<T>(value: unknown, read: () => T): T | null {
  return value === null ? null : read();
}

/**
 * A count the stamp keeps: a whole number from zero up, within the safe integer
 * range. Anything else, text included, is refused by field and never quoted.
 */
function readCount(field: string, value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) return refuse(field, "not_a_count");
  return value;
}

function readLabel(field: string, value: unknown): string {
  if (typeof value !== "string") return refuse(field, "wrong_type");
  if (value.length > LABEL_MAX_CHARS) return refuse(field, "too_long");
  if (value === SHADOW_ARTIFACT_VERSIONS_UNREADABLE) return value;
  const match = LABEL_SHAPE.exec(value);
  if (match === null) return refuse(field, "not_an_id");
  const signed = match[1];
  if (signed !== undefined) {
    // The producer sorts the tail, so a tail that is not sorted did not come from it. `idList` sorts its own copy and
    // refuses a bad shape, an over-long id, too many ids and a repeat, under this field's name.
    const ids = signed.split("+");
    if (idList(field, ids).join("+") !== signed) return refuse(field, "not_an_id");
  }
  return value;
}

function readInstant(value: unknown): Date {
  if (!(value instanceof Date) || !Number.isFinite(value.getTime())) return refuse("at", "bad_instant");
  return value;
}

/**
 * A formatter for the shop's zone, or a refusal. Built here and only here, and
 * the runtime's own RangeError is dropped rather than chained as a `cause`: its
 * message quotes the zone it was given ("Invalid time zone specified: ...").
 */
function zoneFormatter(value: unknown): Intl.DateTimeFormat {
  if (typeof value !== "string") return refuse("shopTimeZone", "wrong_type");
  if (value.length > ZONE_MAX_CHARS) return refuse("shopTimeZone", "too_long");
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: value, weekday: "short", hour: "2-digit", hourCycle: "h23" });
  } catch {
    return refuse("shopTimeZone", "unknown_time_zone");
  }
}

// ---------------------------------------------------------------------------
// The buckets.
// ---------------------------------------------------------------------------

/**
 * The day part and the weekday or weekend flag of an instant, on the shop's own
 * clock. Exported because Q17's tests and the gateway's agreement test want the
 * bucket without a whole stamp. Throws `AttributionStampError` for a bad Date
 * or an unknown zone.
 *
 * The runtime does the zone arithmetic, so a daylight saving change moves the
 * UTC instant of each boundary with the clocks and a skipped or repeated local
 * hour still lands in one bucket. Local midnight is hour 0 (`hourCycle: "h23"`),
 * never 24.
 */
export function attributionTimeBuckets(at: Date, shopTimeZone: string): AttributionTimeBuckets {
  const instant = readInstant(at);
  const parts = zoneFormatter(shopTimeZone).formatToParts(instant);
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const hour = Number.parseInt(parts.find((p) => p.type === "hour")?.value ?? "", 10);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23 || weekday === "") return refuse("shopTimeZone", "unreadable_time");
  const dayPart = ATTRIBUTION_DAY_PARTS[Math.floor(hour / ATTRIBUTION_DAY_PART_HOURS)];
  if (dayPart === undefined) return refuse("shopTimeZone", "unreadable_time");
  return { dayPart, dayType: WEEKEND_WEEKDAYS.includes(weekday) ? "weekend" : "weekday" };
}

// ---------------------------------------------------------------------------
// The stamp.
// ---------------------------------------------------------------------------

/**
 * Builds the stamp, or throws. Pure: no clock, no I/O, no database, nothing
 * written. See the file header for what it refuses and why.
 */
export function attributionStamp<T extends AttributionStampInput>(input: ExactAttributionStampInput<T>): AttributionStamp {
  const data: unknown = input;
  if (typeof data !== "object" || data === null || Array.isArray(data)) return refuse("(input)", "not_an_object");

  // Unknown keys first, in the order they arrive. Reflect.ownKeys, so a symbol
  // key or an own `__proto__` from a parsed JSON object is seen, not skipped.
  const allowed: readonly string[] = [...INPUT_KEYS, ...OPTIONAL_INPUT_KEYS];
  for (const key of Reflect.ownKeys(data)) {
    if (typeof key === "string" && allowed.includes(key)) continue;
    return refuse(typeof key === "string" && NAMED_REFUSED_KEYS.includes(key) ? key : "(unnamed key)", "unknown_key");
  }
  const record = data as Record<string, unknown>;
  for (const key of INPUT_KEYS) {
    if (!Object.hasOwn(record, key)) return refuse(key, "missing_key");
  }

  const scenario = oneOf<Scenario>("scenario", record["scenario"], SCENARIOS);
  const mapVersion = shaped("mapVersion", record["mapVersion"], MAP_VERSION_SHAPE, MAP_VERSION_MAX_CHARS);
  const framework = oneOf<string>("framework", record["framework"], FRAMEWORKS);
  const moveIds = idList("moveIds", record["moveIds"]);
  const signOffIds = idList("signOffIds", record["signOffIds"]);
  const channel = oneOf<Channel>("channel", record["channel"], CHANNELS);
  const entrySourceKind = oneOf<AttributionEntrySourceKind>("entrySourceKind", record["entrySourceKind"], ATTRIBUTION_ENTRY_SOURCE_KINDS);
  const inquiryType = oneOf<InquiryType | "unknown">("inquiryType", record["inquiryType"], INQUIRY_VALUES);
  const { dayPart, dayType } = attributionTimeBuckets(readInstant(record["at"]), record["shopTimeZone"] as string);
  const composeModel = nullable(record["composeModel"], () => shaped("composeModel", record["composeModel"], MODEL_SHAPE, MODEL_MAX_CHARS));
  const configurationLabel = nullable(record["configurationLabel"], () => readLabel("configurationLabel", record["configurationLabel"]));
  const editKind = nullable(record["editKind"], () => oneOf<EditKind>("editKind", record["editKind"], EDIT_KINDS));
  const clean = record["firstPassClean"];
  if (clean !== null && typeof clean !== "boolean") return refuse("firstPassClean", "wrong_type");
  const windowOmitted = nullable(record["windowOmitted"], () => readCount("windowOmitted", record["windowOmitted"]));
  const memoryOmitted = nullable(record["memoryOmitted"], () => readCount("memoryOmitted", record["memoryOmitted"]));

  const experimentArm = readExperimentArm(record["experimentArm"]);

  return Object.freeze({
    scenario,
    mapVersion,
    framework,
    moveIds,
    signOffIds,
    channel,
    entrySourceKind,
    inquiryType,
    dayPart,
    dayType,
    composeModel,
    configurationLabel,
    editKind,
    firstPassClean: clean,
    windowOmitted,
    memoryOmitted,
    ...(experimentArm === null ? {} : { experimentArm }),
  });
}

/**
 * The two omitted counts of a STORED stamp, for a reader that joins forgetting
 * to outcomes. A stamp written before the counts existed has neither key, and
 * absent is reported as `null` (unknown), never `0`: a reader that counted an
 * old stamp as "nothing dropped" would show forgetting as rarer than it was.
 * A key that is present must be a count or null, and anything else throws
 * (the same field-and-reason refusal, no value quoted), because a stored
 * stamp that fails its own rule is corruption to hear about (CLAUDE.md
 * section 0). Pure; builds and changes nothing.
 */
export function attributionStampOmitted(stored: unknown): { readonly windowOmitted: number | null; readonly memoryOmitted: number | null } {
  if (typeof stored !== "object" || stored === null || Array.isArray(stored)) return refuse("(input)", "not_an_object");
  const record = stored as Record<string, unknown>;
  const read = (key: "windowOmitted" | "memoryOmitted"): number | null =>
    !Object.hasOwn(record, key) || record[key] === null ? null : readCount(key, record[key]);
  return { windowOmitted: read("windowOmitted"), memoryOmitted: read("memoryOmitted") };
}
